/**
 * Notifications tab (User Profile)
 * Lets a user set their Telegram handle, connect the Axetra bot, and choose
 * which events they want to be notified about.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from '../../hooks/useUserProfile';

const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white shadow rounded-lg ${className}`}>{children}</div>
);

const CardHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4 border-b border-gray-200">{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold text-gray-900">{children}</h2>
);

const CardContent = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4">{children}</div>
);

/** Accessible on/off switch. */
function Toggle({
  checked,
  onChange,
  disabled = false,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
        checked ? 'bg-indigo-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export default function NotificationsTab() {
  const { t } = useTranslation('profile');
  const { data: settings, isLoading } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();

  const [telegram, setTelegram] = useState('');
  const [notifyPayment, setNotifyPayment] = useState(true);
  const [notifyClickFraud, setNotifyClickFraud] = useState(true);

  // Seed local state once settings load.
  useEffect(() => {
    if (settings) {
      setTelegram(settings.telegram ?? '');
      setNotifyPayment(settings.notify_payment);
      setNotifyClickFraud(settings.notify_click_fraud);
    }
  }, [settings]);

  if (isLoading || !settings) {
    return <div className="text-gray-500">{t('loading')}</div>;
  }

  const connected = settings.telegram_connected;
  const handleForBot = telegram.replace(/^@/, '').trim();
  const botLink = settings.telegram_bot_link;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings.mutate({
      telegram: handleForBot ? handleForBot : null,
      notify_payment: notifyPayment,
      notify_click_fraud: notifyClickFraud,
    });
  };

  return (
    <div className="space-y-6">
      {/* Telegram account */}
      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.telegram_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            {/* Handle */}
            <div>
              <label htmlFor="telegram-handle" className="label">
                {t('notifications.handle_label')}
              </label>
              <div className="mt-1 relative max-w-sm">
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm pointer-events-none">
                  @
                </span>
                <input
                  id="telegram-handle"
                  type="text"
                  autoComplete="off"
                  value={telegram}
                  onChange={(e) => setTelegram(e.target.value)}
                  placeholder={t('notifications.handle_placeholder')}
                  className="input pl-7"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('notifications.handle_hint')}</p>

              {/* Connection status */}
              <div className="mt-3">
                {connected ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    {t('notifications.status_connected')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                    {t('notifications.status_not_connected')}
                  </span>
                )}
              </div>
            </div>

            {/* Notification types */}
            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                {t('notifications.types_title')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('notifications.payment_label')}
                    </div>
                    <p className="text-xs text-gray-500">{t('notifications.payment_description')}</p>
                  </div>
                  <Toggle
                    checked={notifyPayment}
                    onChange={setNotifyPayment}
                    label={t('notifications.payment_label')}
                  />
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('notifications.click_fraud_label')}
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('notifications.click_fraud_description')}
                    </p>
                  </div>
                  <Toggle
                    checked={notifyClickFraud}
                    onChange={setNotifyClickFraud}
                    label={t('notifications.click_fraud_label')}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button type="submit" disabled={updateSettings.isPending} className="btn btn-primary">
                {updateSettings.isPending
                  ? t('notifications.saving')
                  : t('notifications.save_button')}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* How to connect */}
      {!connected && (
        <Card className="border-2 border-indigo-100">
          <CardHeader>
            <CardTitle>{t('notifications.howto_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm text-gray-700 list-decimal list-inside">
              <li>{t('notifications.howto_step1')}</li>
              <li>
                {t('notifications.howto_step2')}{' '}
                {botLink && (
                  <a
                    href={botLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-indigo-600 underline hover:text-indigo-500"
                  >
                    @{settings.telegram_bot_username}
                  </a>
                )}
              </li>
              <li>{t('notifications.howto_step3')}</li>
              <li>{t('notifications.howto_step4')}</li>
            </ol>

            {botLink && (
              <a
                href={botLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary mt-4 inline-block"
              >
                {t('notifications.open_bot_button')}
              </a>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
